/** TanStack Query hook for fetching learner dashboard stats (streak, lessons, learning time, performance). */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import type { StatsResponse } from '@formency/shared';

// ─── Types ─────────────────────────────────────────────────────────────────

/**
 * Time window over which recency-filtered stats are computed — focus areas,
 * accuracy period, and similar. The streak and rolling-learning-time blocks
 * use their own natural windows and aren't affected by this choice.
 */
export type StatsWindow = 1 | 7 | 30 | 90 | 365 | 'all';

const DEFAULT_WINDOW: StatsWindow = 7;

// ─── Query key factory ─────────────────────────────────────────────────────

export const statsKeys = {
  all: ['f', 'stats'] as const,
  me: (windowDays: StatsWindow = DEFAULT_WINDOW) => [...statsKeys.all, 'me', windowDays] as const,
};

// ─── Queries ───────────────────────────────────────────────────────────────

/**
 * Fetch aggregated dashboard stats for the current user (lessons, streak,
 * learning time, performance).
 *
 * Pass `windowDays` to change the recency window for focus areas / accuracy
 * period — default is last 7 days. Legal values: 1, 7, 30, 90, 365, 'all'.
 */
export function useMyStats(options?: {
  enabled?: boolean;
  staleTime?: number;
  windowDays?: StatsWindow;
}) {
  const windowDays = options?.windowDays ?? DEFAULT_WINDOW;
  return useQuery<StatsResponse>({
    queryKey: statsKeys.me(windowDays),
    queryFn: () => api.get<StatsResponse>(`/me/stats?windowDays=${windowDays}`),
    staleTime: 5 * 60_000,
    enabled: options?.enabled,
  });
}
